import React, { useCallback, useEffect, useState } from 'react';
import { Radio } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.shared';

const HISTORY_URL = '/modificari-legislative';
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface TickerItem {
  target_id?: string | null;
  target_label: string;
  target_canonical_ref?: string | null;
  local_table?: string | null;
  relation_id: string;
  publication_date?: string | null;
  effective_date?: string | null;
  modification_types?: string[];
  source: {
    act_type?: string | null;
    act_number?: string | null;
    title?: string | null;
    official_url?: string | null;
  };
}

interface TickerResponse {
  schema_version: 1;
  generated_at: string;
  feed_source?: string;
  items: TickerItem[];
}

const formatDate = (value?: string | null) => {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
};

const sourceLabel = (item: TickerItem, t: (key: string) => string) => {
  const type = item.source.act_type?.trim();
  const number = item.source.act_number?.trim();
  if (type && number) return `${type} nr. ${number}`;
  return type || item.source.title || t('ticker.modifyingAct');
};

const formatModType = (val: string, t: (key: string) => string) => {
  const norm = val.toLowerCase().replace(/_/g, ' ');
  if (norm.includes('modificare')) return t('ticker.modification');
  if (norm.includes('completare')) return t('ticker.completion');
  if (norm.includes('abrogare')) return t('ticker.repeal');
  if (norm.includes('republicare')) return t('ticker.republication');
  if (norm.includes('rectificare')) return t('ticker.rectification');
  return norm;
};

const TickerItems: React.FC<{ items: TickerItem[]; duplicate?: boolean; t: (key: string) => string }> = ({ items, duplicate = false, t }) => (
  <span className="legislative-ticker__group" aria-hidden={duplicate || undefined}>
    {items.map((item) => {
      const types = (item.modification_types || []).map((value) => formatModType(value, t)).join(', ');
      const date = formatDate(item.publication_date || item.effective_date);
      return (
        <Link
          key={`${duplicate ? 'copy-' : ''}${item.relation_id}`}
          to={`${HISTORY_URL}?modificare=${encodeURIComponent(item.relation_id)}#modificare`}
          className="legislative-ticker__item"
          tabIndex={duplicate ? -1 : undefined}
        >
          <strong>{item.target_label}</strong>
          <span aria-hidden="true"> — </span>
          <span>{sourceLabel(item, t)}</span>
          {types && <span className="legislative-ticker__meta"> · {types}</span>}
          {date && <time dateTime={item.publication_date || item.effective_date || undefined}> · {date}</time>}
          <span className="legislative-ticker__dot" aria-hidden="true">•</span>
        </Link>
      );
    })}
  </span>
);

const LegislativeTicker: React.FC = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState<TickerItem[]>([]);

  const loadItems = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch('/api/coduri/legislative-history/ticker', {
        cache: 'no-store',
        signal,
      });
      if (!response.ok) return;
      const payload = await response.json() as TickerResponse;
      if (Array.isArray(payload.items)) {
        setItems(payload.items);
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        // Keep previously confirmed archive entries on a transient failure
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void loadItems(controller.signal);
    const interval = window.setInterval(() => void loadItems(), REFRESH_INTERVAL_MS);
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void loadItems();
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [loadItems]);

  const displayItems = items;

  return (
    <aside className="legislative-ticker" aria-label={t('ticker.ariaLabel')}>
      <Link className="legislative-ticker__label" to={HISTORY_URL}>
        <Radio size={16} aria-hidden="true" className="lg:hidden" />
        <span className="hidden lg:inline-block w-2 h-2 rounded-full bg-[#5b16f3] mr-1 flex-shrink-0" aria-hidden="true" />
        <span className="legislative-ticker__label-wide">{t('ticker.latestChanges')}</span>
        <span className="legislative-ticker__label-short">{t('ticker.news')}</span>
      </Link>
      <div className="legislative-ticker__viewport">
        <div className="legislative-ticker__track">
          <TickerItems items={displayItems} t={t} />
          <TickerItems items={displayItems} t={t} duplicate />
        </div>
      </div>
      <Link
        to={HISTORY_URL}
        className="inline-flex items-center gap-1.5 px-3 lg:px-5 text-xs font-bold text-ai hover:text-ai-hover whitespace-nowrap bg-surface z-20 flex-shrink-0 transition-colors border-l border-border h-full "
      >
        {t('ticker.viewAll')} <span aria-hidden="true">→</span>
      </Link>
    </aside>
  );
};

export default LegislativeTicker;
